import React from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { useCompany } from "@/context/CompanyContext";
import { apiClient } from "@/lib/apiClient";
import { buildAiSuggestions } from "@/lib/aiSuggestions";
import {
  FileText,
  Receipt as ReceiptIcon,
  FileInput,
  Users,
  LayoutDashboard,
  Landmark,
  Wallet,
  BarChart3,
  Settings as SettingsIcon,
  Bell,
  Sparkles,
  BookOpen,
  History,
} from "lucide-react";

// Statische "Jump-to" Ziele (immer sichtbar, wenn keine Query eingegeben wurde)
const QUICK_NAV = [
  { label: "Dashboard", url: "/", icon: LayoutDashboard, testid: "cmdk-nav-dashboard" },
  { label: "Belege", url: "/receipts", icon: ReceiptIcon, testid: "cmdk-nav-receipts" },
  { label: "Rechnungen", url: "/invoices", icon: FileText, testid: "cmdk-nav-invoices" },
  { label: "Eingangsrechnungen", url: "/bills", icon: FileInput, testid: "cmdk-nav-bills" },
  { label: "Bank-Abgleich", url: "/bank-reconciliation", icon: Landmark, testid: "cmdk-nav-bank" },
  { label: "Mahnwesen", url: "/dunning", icon: Bell, testid: "cmdk-nav-dunning" },
  { label: "Kasse", url: "/cash", icon: Wallet, testid: "cmdk-nav-cash" },
  { label: "Auswertungen", url: "/reports", icon: BarChart3, testid: "cmdk-nav-reports" },
  { label: "Kunden", url: "/customers", icon: Users, testid: "cmdk-nav-customers" },
  { label: "Einstellungen", url: "/settings", icon: SettingsIcon, testid: "cmdk-nav-settings" },
];

const TYPE_ICON = {
  invoice: FileText,
  bill: FileInput,
  receipt: ReceiptIcon,
  contact: Users,
  journal: BookOpen,
};

const TYPE_LABEL = {
  invoice: "Rechnungen",
  bill: "Eingangsrechnungen",
  receipt: "Belege",
  contact: "Kontakte",
  journal: "Buchungen & Zahlungen",
};

function formatAmount(h) {
  if (h.amount == null) return "";
  try {
    return `${h.currency || "CHF"} ${Number(h.amount).toLocaleString("de-CH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  } catch {
    return `${h.currency || ""} ${h.amount}`;
  }
}

export default function CommandPalette({ open, onOpenChange }) {
  const navigate = useNavigate();
  const { company } = useCompany();
  const [query, setQuery] = React.useState("");
  const [hits, setHits] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [period, setPeriod] = React.useState("all");
  const [sort, setSort] = React.useState("relevance");
  const [amountLabel, setAmountLabel] = React.useState(null);

  const periodRange = (key) => {
    const d = new Date(); const y = d.getFullYear(); const m = d.getMonth();
    const iso = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
    switch (key) {
      case "month": return [iso(new Date(y, m, 1)), iso(new Date(y, m + 1, 0))];
      case "last_month": return [iso(new Date(y, m - 1, 1)), iso(new Date(y, m, 0))];
      case "quarter": { const qs = Math.floor(m / 3) * 3; return [iso(new Date(y, qs, 1)), iso(new Date(y, qs + 3, 0))]; }
      case "year": return [`${y}-01-01`, `${y}-12-31`];
      case "last_year": return [`${y - 1}-01-01`, `${y - 1}-12-31`];
      default: return [null, null];
    }
  };
  const PERIODS = [["all", "Alle"], ["month", "Dieser Monat"], ["last_month", "Letzter Monat"], ["quarter", "Dieses Quartal"], ["year", "Dieses Jahr"], ["last_year", "Letztes Jahr"]];
  const debounceRef = React.useRef(null);

  // Reset when closed
  React.useEffect(() => {
    if (!open) {
      setQuery("");
      setHits([]);
    }
  }, [open]);

  // Debounced search
  React.useEffect(() => {
    if (!open) return undefined;
    if (!company?._id && !company?.id) return undefined;
    const q = query.trim();
    if (!q) {
      setHits([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const cid = company._id || company.id;
        const [from, to] = periodRange(period);
        const { data } = await apiClient.get(`/companies/${cid}/search`, {
          params: { q, limit: 30, sort, ...(from ? { date_from: from, date_to: to } : {}) },
        });
        setHits(data?.hits || []);
        setAmountLabel(data?.amount_label || null);
      } catch (e) {
        console.error("search failed", e);
        setHits([]);
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, open, company, period, sort]);

  const histKey = `search.history.${company?._id || "x"}`;
  const [history, setHistory] = React.useState(() => { try { return JSON.parse(localStorage.getItem(histKey) || "[]"); } catch { return []; } });
  React.useEffect(() => { try { setHistory(JSON.parse(localStorage.getItem(histKey) || "[]")); } catch { setHistory([]); } }, [histKey]);
  const rememberSearch = (term) => {
    const t = String(term || "").trim();
    if (t.length < 2) return;
    const next = [t, ...history.filter((h) => h.toLowerCase() !== t.toLowerCase())].slice(0, 5);
    setHistory(next);
    localStorage.setItem(histKey, JSON.stringify(next));
  };
  const clearHistory = () => { setHistory([]); localStorage.removeItem(histKey); };

  const go = (url) => {
    if (hasQuery) rememberSearch(query);
    onOpenChange(false);
    navigate(url);
  };

  // Gruppieren der hits nach type
  const grouped = hits.reduce((acc, h) => {
    (acc[h.type] ||= []).push(h);
    return acc;
  }, {});

  const hasQuery = query.trim().length > 0;

  return (
    <CommandDialog open={open} onOpenChange={(o) => { if (!o && hasQuery && hits.length > 0) rememberSearch(query); onOpenChange(o); }} commandProps={{ shouldFilter: false }}>
      <CommandInput
        placeholder="Suche nach Firma, Beleg, Rechnung, Zahlung oder Betrag (139.95, ≈140) oder Datum (5.10.2026)…"
        value={query}
        onValueChange={setQuery}
        data-testid="command-palette-input"
      />
      {hasQuery && (
        <div className="flex flex-wrap items-center gap-1 px-3 py-1.5 border-b border-slate-100 text-[11px]" data-testid="command-palette-filters">
          {PERIODS.map(([k, l]) => (
            <button key={k} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => setPeriod(k)}
              className={`rounded-full px-2 py-0.5 border transition-colors ${period === k ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
              data-testid={`cmdk-period-${k}`}>{l}</button>
          ))}
          <span className="mx-1 text-slate-300">|</span>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => setSort((v) => (v === "date" ? "relevance" : "date"))}
            className={`rounded-full px-2 py-0.5 border transition-colors ${sort === "date" ? "bg-indigo-600 text-white border-indigo-600" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
            title="Treffer nach Datum (neueste zuerst) statt nach Relevanz sortieren" data-testid="cmdk-sort-date">
            {sort === "date" ? "Sortiert: Datum ↓" : "Nach Datum sortieren"}
          </button>
          {amountLabel && (
            <span className="ml-auto rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-0.5" data-testid="cmdk-amount-hint">{amountLabel.startsWith("Datum") ? "Datumssuche: " : "Betragssuche: "}{amountLabel.replace(/^Datum /, "")}</span>
          )}
        </div>
      )}
      <CommandList data-testid="command-palette-list">
        {!hasQuery && history.length > 0 && (
          <CommandGroup heading="Letzte Suchen">
            {history.map((h, i) => (
              <CommandItem key={`hist-${h}`} value={`hist-${h}`} onSelect={() => setQuery(h)} data-testid={`cmdk-history-${i}`}>
                <History className="h-4 w-4 mr-2 text-slate-400" />
                <span className="flex-1">{h}</span>
                {i === 0 && (
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={(e) => { e.stopPropagation(); clearHistory(); }}
                    className="text-[11px] text-slate-400 hover:text-slate-700 hover:underline" data-testid="cmdk-history-clear">Verlauf löschen</button>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {!hasQuery && (
          <CommandGroup heading="Schnellzugriff">
            {QUICK_NAV.map((item) => (
              <CommandItem
                key={item.url}
                value={`nav-${item.label}`}
                onSelect={() => go(item.url)}
                data-testid={item.testid}
              >
                <item.icon className="h-4 w-4 mr-2 text-slate-500" />
                <span>{item.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {hasQuery && loading && (
          <div className="py-6 text-center text-sm text-slate-500">
            Suche läuft...
          </div>
        )}

        {/* Iter105: AI Smart-Actions */}
        {hasQuery && (() => {
          const suggestions = buildAiSuggestions(query);
          if (suggestions.length === 0) return null;
          return (
            <CommandGroup heading="💡 Smart-Aktionen">
              {suggestions.map((s) => (
                <CommandItem
                  key={s.id}
                  value={`ai-${s.id}-${s.label}`}
                  onSelect={() => go(s.url)}
                  data-testid={`cmdk-ai-${s.id}`}
                >
                  <Sparkles className="h-4 w-4 mr-2 text-violet-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-900 truncate">{s.label}</div>
                    {s.hint && (
                      <div className="text-[11px] text-violet-700 truncate">
                        {s.hint}
                      </div>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          );
        })()}

        {hasQuery && !loading && hits.length === 0 && (
          <CommandEmpty>Keine Ergebnisse für "{query}".</CommandEmpty>
        )}

        {hasQuery &&
          Object.keys(grouped).map((type) => {
            const items = grouped[type];
            const Icon = TYPE_ICON[type] || FileText;
            return (
              <CommandGroup
                key={type}
                heading={`${TYPE_LABEL[type] || type} (${items.length})`}
              >
                {items.map((h) => (
                  <CommandItem
                    key={`${h.type}-${h.id}`}
                    value={`${h.type}-${h.id}-${h.title}-${h.subtitle || ""}`}
                    onSelect={() => go(h.url)}
                    data-testid={`cmdk-hit-${h.type}-${h.id}`}
                  >
                    <Icon className="h-4 w-4 mr-2 text-slate-500 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-900 truncate">
                        {h.title}
                      </div>
                      {h.subtitle && (
                        <div className="text-xs text-slate-500 truncate">
                          {h.subtitle}
                        </div>
                      )}
                      {h.snippet && (
                        <div
                          className="text-[11px] text-slate-600 italic truncate mt-0.5"
                          data-testid={`cmdk-snippet-${h.type}-${h.id}`}
                          dangerouslySetInnerHTML={{
                            __html: h.snippet.replace(
                              /\*\*\*([^*]+)\*\*\*/g,
                              '<mark class="bg-amber-200 not-italic font-medium px-0.5 rounded">$1</mark>'
                            ),
                          }}
                        />
                      )}
                    </div>
                    {h.amount != null && (
                      <span className="text-xs font-mono text-slate-600 ml-3 shrink-0">
                        {formatAmount(h)}
                      </span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            );
          })}
      </CommandList>
    </CommandDialog>
  );
}
